import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Pencil, History } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { API, ax, errorText, MARKETPLACES } from "./common";

const EMPTY = { code: "", name: "", source_warehouse_id: "", destination_warehouse_id: "", marketplace: MARKETPLACES[0] };

export default function RouteProfilesPanel({ tenant, showMsg, refreshTick = 0 }) {
  const { t } = useLang();
  const base = `${API}/tenants/${tenant.id}/route-profiles`;
  const [profiles, setProfiles] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [versions, setVersions] = useState(null);

  const load = useCallback(async (silent = false) => {
    try {
      const [p, w] = await Promise.all([axios.get(base, ax), axios.get(`${API}/warehouses?active=true`, ax)]);
      setProfiles(p.data);
      setWarehouses(w.data);
    } catch (e) { if (!silent) showMsg("error", errorText(e, t("adm.error"))); }
  }, [base, showMsg, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (refreshTick) load(true); }, [refreshTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const nameOf = (id) => warehouses.find(w => w.id === id)?.name || id;
  const senders = warehouses.filter(w => w.capabilities?.includes("SENDER"));
  const receivers = warehouses.filter(w => w.capabilities?.includes("RECEIVER"));

  const openCreate = () => { setEditingId(null); setForm({ ...EMPTY }); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({ code: p.code, name: p.name, source_warehouse_id: p.source_warehouse_id,
              destination_warehouse_id: p.destination_warehouse_id, marketplace: p.marketplace });
  };

  const save = async () => {
    try {
      if (editingId) {
        const { code, ...changes } = form;
        await axios.put(`${base}/${editingId}`, changes, ax);
        showMsg("success", t("adm.rp.updated"));
      } else {
        await axios.post(base, form, ax);
        showMsg("success", t("adm.rp.created"));
      }
      setForm(null);
      load();
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
  };

  const setActive = async (p, active) => {
    try {
      await axios.post(`${base}/${p.id}/${active ? "activate" : "deactivate"}`, {}, ax);
      showMsg("success", t("adm.rp.status_changed"));
      load();
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
  };

  const showVersions = async (p) => {
    try {
      const { data } = await axios.get(`${base}/${p.id}/versions`, ax);
      setVersions({ profile: p, rows: data });
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
  };

  const valid = form && form.name.trim() && form.source_warehouse_id && form.destination_warehouse_id && (editingId || form.code.trim());

  const warehouseSelect = (field, options) => (
    <Select value={form[field]} onValueChange={v => setForm(f => ({ ...f, [field]: v }))}>
      <SelectTrigger className="h-10"><SelectValue placeholder={t("adm.rp.select_warehouse")} /></SelectTrigger>
      <SelectContent>
        {options.map(w => <SelectItem key={w.id} value={w.id}>{w.name} ({w.code})</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-800">{t("adm.rp.title")}</h3>
        <Button size="sm" onClick={openCreate} className="gap-1.5 bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4" /> {t("adm.rp.new")}
        </Button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm divide-y divide-slate-50">
        {profiles.length === 0 && <p className="p-6 text-center text-slate-400 text-sm">{t("adm.rp.empty")}</p>}
        {profiles.map(p => (
          <div key={p.id} className="flex items-center gap-3 px-4 py-3">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900 text-sm truncate">
                {p.name} <span className="font-mono text-xs text-slate-400">{p.code} · v{p.version}</span>
              </p>
              <p className="text-xs text-slate-500">
                {nameOf(p.source_warehouse_id)} → {nameOf(p.destination_warehouse_id)} · {t(`adm.marketplace.${p.marketplace}`)}
              </p>
            </div>
            <span className={`text-xs font-medium px-2 py-1 rounded-lg ${p.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
              {t(p.active ? "adm.active" : "adm.inactive")}
            </span>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600" onClick={() => showVersions(p)} aria-label={t("adm.rp.versions")}>
              <History className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600" onClick={() => openEdit(p)} aria-label={t("common.edit")}>
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setActive(p, !p.active)}>
              {t(p.active ? "adm.deactivate" : "adm.activate")}
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={!!form} onOpenChange={(open) => !open && setForm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t(editingId ? "adm.rp.edit" : "adm.rp.new")}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>{t("adm.code")} *</Label>
                  <Input value={form.code} disabled={!!editingId} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} className="h-10 font-mono uppercase" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("common.name")} *</Label>
                  <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-10" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>{t("adm.rp.source")} *</Label>
                {warehouseSelect("source_warehouse_id", senders)}
              </div>
              <div className="space-y-1.5">
                <Label>{t("adm.rp.destination")} *</Label>
                {warehouseSelect("destination_warehouse_id", receivers)}
              </div>
              <div className="space-y-1.5">
                <Label>{t("adm.rp.marketplace")} *</Label>
                <Select value={form.marketplace} onValueChange={v => setForm(f => ({ ...f, marketplace: v }))}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MARKETPLACES.map(m => <SelectItem key={m} value={m}>{t(`adm.marketplace.${m}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>{t("common.cancel")}</Button>
            <Button onClick={save} disabled={!valid} className="bg-blue-600 hover:bg-blue-700">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!versions} onOpenChange={(open) => !open && setVersions(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("adm.rp.versions")} · {versions?.profile.code}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {versions?.rows.map(v => (
              <div key={v.id} className="text-xs border border-slate-100 rounded-lg p-2">
                <p className="font-semibold text-slate-800">v{v.version} · {new Date(v.created_at).toLocaleString()}</p>
                <p className="text-slate-500">
                  {nameOf(v.snapshot.source_warehouse_id)} → {nameOf(v.snapshot.destination_warehouse_id)} · {t(`adm.marketplace.${v.snapshot.marketplace}`)}
                </p>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVersions(null)}>{t("common.close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
